/**
 * 颜色 Token 工具（FR-029, 2026-07-11）
 *
 * 职责：
 *   - 从 :root / [data-theme="day"] 读取 CSS 变量真色值
 *   - 提供给 Canvas 2D 上下文 fillStyle/strokeStyle 使用
 *   - 因为 canvas API 读不到 CSS var，必须走 getComputedStyle
 *
 * 使用：
 *   import { tokenColor, TOKEN_MAP } from '../utils/color-tokens';
 *   ctx.fillStyle = tokenColor('--tactical-cyan');  // Day 模式返回 #00B4CC
 *
 * 性能：
 *   - 模块加载时缓存所有 token 值（getComputedStyle × 1）
 *   - 后续 O(1) Map 查表
 */

/** CSS 变量名 → 解析后的真色值 */
const cache = new Map<string, string>();

/** 模块加载时从 :root 读取所有 token */
function refreshCache(): void {
  if (typeof document === "undefined") return;
  const styles = getComputedStyle(document.documentElement);
  // Ardot v2.0 tokens
  const ardotTokens = [
    "--tactical-cyan",       // #00E4FF → Day #00B4CC
    "--valorant-red",        // #FF4655 → Day #C4283A
    "--tactical-amber",      // #F8B700 → Day #B89A00
    "--tactical-green",      // #7FD962 → Day #4A9E3F
    "--bg-void",             // #0F1923 → Day #F2F4F7
    "--bg-deep",             // #0F1218 → Day #E0E3E8
    "--bg-elevated",         // #1F2A35 → Day #FFFFFF
    "--cover-bg",            // #2A3540 → Day #E0E3E8
    "--hp-bar-frame",        // #3A4050 → Day #E0E3E8
    "--text-primary",        // #E0E6F0 → Day #1C1E24
    "--text-secondary",      // #9AA0AA → Day #5A6678
    "--text-muted",          // #6B7585 → Day #8A95A8
    "--tactical-cyan-hi",    // (Day only) #008494
    "--valorant-red-hi",     // (Day only) #B2212F
    "--tactical-amber-hi",   // (Day only) #8C7300
    // V14 地图 Canvas 专用 token（2026-07-28 地图提亮）
    "--map-bg",
    "--map-empty",
    "--map-cover",
    "--map-cover-stroke",
    "--map-obstacle",
    "--map-obstacle-stroke",
    "--map-spawn-a",
    "--map-spawn-a-stroke",
    "--map-spawn-b",
    "--map-spawn-b-stroke",
    "--map-unreachable",
    "--map-unreachable-stroke",
    "--map-grid-fine",
    "--map-grid-major",
    "--map-midline",
    // Legacy tokens (Day 覆盖)
    "--color-bg",
    "--color-bg-elev",
    "--color-bg-elev2",
    "--color-text",
    "--color-text-dim",
    "--color-text-strong",
    "--color-team-a",
    "--color-team-b",
    "--color-fire",
    "--color-error",
    "--color-warn",
    "--color-success",
    "--color-cover",
  ];
  for (const name of ardotTokens) {
    const value = styles.getPropertyValue(name).trim();
    if (value) cache.set(name, value);
  }
}

/** 获取 CSS 变量真色值（缓存兜底） */
export function tokenColor(name: string, fallback?: string): string {
  const cached = cache.get(name);
  if (cached) return cached;
  // 兜底：实时读取一次
  if (typeof document !== "undefined") {
    const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    if (value) {
      cache.set(name, value);
      return value;
    }
  }
  return fallback ?? "";
}

/**
 * 预设颜色常量映射 — 供 Canvas 绘图用
 * Day 模式通过 getComputedStyle 自动适配
 */
export const TOKEN_MAP = {
  /** 战术青（玩家/重点/队 A） */
  cyan: () => tokenColor("--tactical-cyan", "#00E4FF"),
  /** 签名红（敌人/危险/队 B） */
  red: () => tokenColor("--valorant-red", "#FF4655"),
  /** 琥珀金（警告/冷却） */
  amber: () => tokenColor("--tactical-amber", "#F8B700"),
  /** 绿（成功/OPENING） */
  green: () => tokenColor("--tactical-green", "#7FD962"),
  /** 页面底色 */
  bgVoid: () => tokenColor("--bg-void", "#0F1923"),
  /** 深色背景 */
  bgDeep: () => tokenColor("--bg-deep", "#0F1218"),
  /** 抬升背景 */
  bgElevated: () => tokenColor("--bg-elevated", "#1F2A35"),
  /** 次文字 */
  textSecondary: () => tokenColor("--text-secondary", "#9AA0AA"),
  /** 弱文字 */
  textMuted: () => tokenColor("--text-muted", "#6B7585"),
  /** 主文字 */
  textPrimary: () => tokenColor("--text-primary", "#E0E6F0"),
  /** Legacy 兼容 */
  colorBg: () => tokenColor("--color-bg", "#0A0E14"),
  colorBgElev: () => tokenColor("--color-bg-elev", "#141A24"),
  colorBgElev2: () => tokenColor("--color-bg-elev2", "#1A2030"),
  colorText: () => tokenColor("--color-text", "#E5E9F0"),
  colorTextDim: () => tokenColor("--color-text-dim", "#6C7480"),
  colorTeamA: () => tokenColor("--color-team-a", "#4EC9B0"),
  colorTeamB: () => tokenColor("--color-team-b", "#F25C54"),
  colorFire: () => tokenColor("--color-fire", "#FFA630"),
  colorError: () => tokenColor("--color-error", "#F25C54"),
  colorWarn: () => tokenColor("--color-warn", "#F2C94C"),
  colorSuccess: () => tokenColor("--color-success", "#4EC9B0"),
  colorCover: () => tokenColor("--color-cover", "rgba(78,201,176,0.6)"),
  /** Day 高对比度 */
  cyanHi: () => tokenColor("--tactical-cyan-hi", "#008494"),
  redHi: () => tokenColor("--valorant-red-hi", "#B2212F"),
  amberHi: () => tokenColor("--tactical-amber-hi", "#8C7300"),
  /** V14 地图 Canvas 专用色（2026-07-28 地图提亮） */
  mapBg: () => tokenColor("--map-bg", "#EDF0F5"),
  mapEmpty: () => tokenColor("--map-empty", "#F4F6F9"),
  mapCover: () => tokenColor("--map-cover", "#DEE1E6"),
  mapCoverStroke: () => tokenColor("--map-cover-stroke", "#8AB4F8"),
  mapObstacle: () => tokenColor("--map-obstacle", "#A4A9B0"),
  mapObstacleStroke: () => tokenColor("--map-obstacle-stroke", "#E85D5D"),
  mapSpawnA: () => tokenColor("--map-spawn-a", "#DBE7FF"),
  mapSpawnB: () => tokenColor("--map-spawn-b", "#FFDBDD"),
  mapUnreachable: () => tokenColor("--map-unreachable", "rgba(255,77,79,0.15)"),
  mapGridFine: () => tokenColor("--map-grid-fine", "#DCE0E6"),
  mapGridMajor: () => tokenColor("--map-grid-major", "#C5CBD4"),
  mapMidline: () => tokenColor("--map-midline", "#A4ACB8"),
} as const;

/**
 * FR-029: 主题切换时刷新缓存
 * 调用方在收到 theme-change 事件后调用
 */
export function refreshColorTokens(): void {
  cache.clear();
  refreshCache();
}

/**
 * FR-029: 取 token 真色值并转 rgba
 * 用于 Canvas 半透明叠加场景（如 hover 预览、镜像标记）
 */
export function tokenColorRgba(name: string, alpha: number, fallback?: string): string {
  const hex = tokenColor(name, fallback);
  if (!hex || !hex.startsWith("#") || hex.length < 7) return `rgba(0,0,0,${alpha})`;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

// 模块加载时初始化
refreshCache();
